import { env } from '$lib/config/env';
import type { Bounds } from '$lib/core/geom';
import {
  canDeleteComment,
  commentTextProblem,
  withoutComment,
  type CommentAuthor,
  type CommentThread,
} from '$lib/core/model';
import { ApiClient, type SignIn } from './api-client';
import { safeStorage } from './storage';

/** One drawing's threads, and the signed-in person's id, which their own comments carry. */
export interface Comments {
  readonly threads: readonly CommentThread[];
  readonly you: string;
}

/**
 * Storage boundary for a drawing's comments, kept apart from the drawing, so a comment never
 * makes a revision. Every call answers with the threads as they stand after it.
 */
export interface CommentStore {
  load(drawingId: string): Promise<Comments>;
  /** `zoom` is where the view was when the box was drawn, so it can return there. */
  start(drawingId: string, region: Bounds, text: string, zoom?: number): Promise<Comments>;
  reply(drawingId: string, threadId: string, text: string): Promise<Comments>;
  setClosed(drawingId: string, threadId: string, closed: boolean): Promise<Comments>;
  remove(drawingId: string, threadId: string, commentId: string): Promise<Comments>;
}

/** Comments on the server, through `/api/comments`. */
export class RemoteCommentStore implements CommentStore {
  private readonly base = `${env.apiBaseUrl}/comments`;
  private readonly api: ApiClient;

  constructor(storage: Storage | null = safeStorage(), signIn: SignIn | null = null) {
    this.api = new ApiClient(storage, signIn);
  }

  load(drawingId: string): Promise<Comments> {
    return this.request(drawingId);
  }

  start(drawingId: string, region: Bounds, text: string, zoom?: number): Promise<Comments> {
    return this.request(drawingId, '', 'POST', { text, region, zoom });
  }

  reply(drawingId: string, threadId: string, text: string): Promise<Comments> {
    return this.request(drawingId, '', 'POST', { text, thread: threadId });
  }

  setClosed(drawingId: string, threadId: string, closed: boolean): Promise<Comments> {
    return this.request(drawingId, '', 'PATCH', { thread: threadId, closed });
  }

  remove(drawingId: string, threadId: string, commentId: string): Promise<Comments> {
    const query = new URLSearchParams({ thread: threadId, comment: commentId });
    return this.request(drawingId, `?${query}`, 'DELETE');
  }

  private async request(
    drawingId: string,
    query = '',
    method = 'GET',
    body?: unknown,
  ): Promise<Comments> {
    const url = `${this.base}/${encodeURIComponent(drawingId)}${query}`;
    return (await this.api.read(await this.api.send(url, method, body))) as Comments;
  }
}

/** The same rules in memory, for tests, as the person `you`. */
export class MemoryCommentStore implements CommentStore {
  private readonly stored = new Map<string, CommentThread[]>();
  private clock = 0;

  constructor(readonly you = 'me') {}

  async load(drawingId: string): Promise<Comments> {
    return this.answer(drawingId);
  }

  async start(drawingId: string, region: Bounds, text: string, zoom?: number): Promise<Comments> {
    const thread = {
      id: `thread-${this.next()}`,
      region,
      ...(zoom === undefined ? {} : { zoom }),
      closed: false,
      comments: [],
    };
    this.stored.set(drawingId, [...this.threads(drawingId), thread]);
    return this.add(drawingId, thread.id, { kind: 'person', id: this.you, name: this.you }, text);
  }

  async reply(drawingId: string, threadId: string, text: string): Promise<Comments> {
    return this.add(drawingId, threadId, { kind: 'person', id: this.you, name: this.you }, text);
  }

  /** What an agent's reply over MCP does, for tests. */
  agentReply(drawingId: string, threadId: string, text: string): Promise<Comments> {
    return Promise.resolve(this.add(drawingId, threadId, { kind: 'ai', name: null }, text));
  }

  async setClosed(drawingId: string, threadId: string, closed: boolean): Promise<Comments> {
    this.change(drawingId, (threads) =>
      threads.map((thread) => (thread.id === threadId ? { ...thread, closed } : thread)),
    );
    return this.answer(drawingId);
  }

  async remove(drawingId: string, threadId: string, commentId: string): Promise<Comments> {
    const thread = this.threads(drawingId).find((each) => each.id === threadId);
    const comment = thread?.comments.find((each) => each.id === commentId);
    if (!comment || !canDeleteComment(comment, this.you))
      throw new Error('The server answered 400');
    this.change(drawingId, (threads) => withoutComment(threads, threadId, commentId));
    return this.answer(drawingId);
  }

  private add(drawingId: string, threadId: string, author: CommentAuthor, text: string): Comments {
    if (commentTextProblem(text)) throw new Error('The server answered 400');
    const comment = { id: `comment-${this.next()}`, author, at: this.clock, text };
    this.change(drawingId, (threads) =>
      threads.map((thread) =>
        thread.id === threadId ? { ...thread, comments: [...thread.comments, comment] } : thread,
      ),
    );
    return this.answer(drawingId);
  }

  private next(): number {
    return (this.clock += 1);
  }

  private threads(drawingId: string): CommentThread[] {
    return this.stored.get(drawingId) ?? [];
  }

  private change(drawingId: string, fn: (threads: CommentThread[]) => CommentThread[]): void {
    this.stored.set(drawingId, fn(this.threads(drawingId)));
  }

  private answer(drawingId: string): Comments {
    return { threads: this.threads(drawingId), you: this.you };
  }
}
